import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDeficiencyLibrary } from '../components/library/useDeficiencyLibrary';
import { ConfirmModal } from '../components/templates/ConfirmModal';
import { AutosaveIndicator } from '../components/templates/AutosaveIndicator';
import type { DeficiencyEntry } from '../api/deficiency-entries';
import { libraryStartersApi, type SuggestionRow } from '../api/library-starters';
import { StarterSectionCard } from './DeficiencyLibraryPage/Starters';
import { SectionCard } from './DeficiencyLibraryPage/SectionCard';
import { AddSection, EmptyState } from './DeficiencyLibraryPage/EmptyState';
import { LibrarySkeleton } from './DeficiencyLibraryPage/LibrarySkeleton';

type Confirm = { kind: 'delete'; entry: DeficiencyEntry } | null;

export default function DeficiencyLibraryPage() {
  const { entries, loading, error, saveState, reload, create, update, remove, reorder } =
    useDeficiencyLibrary();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [activeTradeTag, setActiveTradeTag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newSectionDraft, setNewSectionDraft] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);

  const [introDismissed, setIntroDismissed] = useState<boolean | null>(null);
  const [starters, setStarters] = useState<SuggestionRow[] | null>(null);
  const [expandedStarterSections, setExpandedStarterSections] = useState<Set<string>>(new Set());
  // Single open starter at a time — matches the EntryRow expand pattern on
  // the owned-entry side so the page never shows two stacked previews.
  const [expandedStarterId, setExpandedStarterId] = useState<string | null>(null);
  const [cloningId, setCloningId] = useState<string | null>(null);
  const suggestionsAnchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [intro, suggestions] = await Promise.all([
          libraryStartersApi.getIntro(),
          libraryStartersApi.listSuggestions({}),
        ]);
        if (cancelled) return;
        setIntroDismissed(intro.dismissed);
        setStarters(suggestions.filter((r) => r.kind === 'starter'));
      } catch {
        if (!cancelled) {
          setIntroDismissed(true);
          setStarters([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When an inspector has no owned entries but starters *are* available
  // (the post-#55 seed didn't run, or they haven't personalized anything
  // yet), surface the starter pack as the primary content instead of an
  // empty state. Auto-expand every starter section once so the page reads
  // as "here's your library" rather than "your library is empty."
  const seededStartersOnly = entries.length === 0 && (starters?.length ?? 0) > 0;
  const didAutoExpandStartersRef = useRef(false);
  useEffect(() => {
    if (!seededStartersOnly || didAutoExpandStartersRef.current) return;
    if (!starters || starters.length === 0) return;
    const names = new Set(starters.map((s) => s.sectionName));
    setExpandedStarterSections((prev) => {
      const next = new Set(prev);
      names.forEach((n) => next.add(n));
      return next;
    });
    didAutoExpandStartersRef.current = true;
  }, [seededStartersOnly, starters]);

  const handleDismissIntro = useCallback(async () => {
    setIntroDismissed(true);
    try {
      await libraryStartersApi.dismissIntro();
    } catch {
      // Non-fatal — the banner is dismissed locally either way.
    }
  }, []);

  const handleBrowseSuggestions = useCallback(() => {
    suggestionsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleCloneStarter = useCallback(async (starterId: string) => {
    if (cloningId) return;
    setCloningId(starterId);
    try {
      await libraryStartersApi.clone(starterId);
      setStarters((prev) => prev?.filter((r) => r.id !== starterId) ?? null);
      await reload();
    } finally {
      setCloningId(null);
    }
  }, [cloningId, reload]);

  const toggleStarterSection = useCallback((name: string) => {
    setExpandedStarterSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const starterGroups = useMemo(() => {
    const bySection = new Map<string, SuggestionRow[]>();
    for (const s of starters ?? []) {
      const list = bySection.get(s.sectionName) ?? [];
      list.push(s);
      bySection.set(s.sectionName, list);
    }
    return [...bySection.entries()]
      .map(([name, items]) => ({ name, items }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [starters]);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const isFiltering = normalizedSearch.length > 0 || activeTradeTag !== null;

  const filteredEntries = useMemo(() => {
    let rows = entries;
    if (activeTradeTag) {
      rows = rows.filter((e) => e.tradeTag === activeTradeTag);
    }
    if (normalizedSearch) {
      rows = rows.filter((e) => {
        const hay = [
          e.title,
          e.description ?? '',
          e.componentName ?? '',
          e.tradeTag ?? '',
          e.sectionName,
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(normalizedSearch);
      });
    }
    return rows;
  }, [entries, normalizedSearch, activeTradeTag]);

  const grouped = useMemo(() => {
    const bySection = new Map<string, DeficiencyEntry[]>();
    for (const e of filteredEntries) {
      const list = bySection.get(e.sectionName) ?? [];
      list.push(e);
      bySection.set(e.sectionName, list);
    }
    for (const list of bySection.values()) {
      list.sort((a, b) => a.orderIndex - b.orderIndex || a.title.localeCompare(b.title));
    }
    const sections = [...bySection.entries()]
      .map(([name, items]) => ({ name, items }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return sections;
  }, [filteredEntries]);

  // Sections + counts for the chip strip. Use the *unfiltered* list so chips
  // don't disappear out from under the user as they type, but badge the
  // post-filter counts so the chips double as a preview of what's there.
  const allSectionNames = useMemo(() => {
    const names = new Set<string>();
    for (const e of entries) names.add(e.sectionName);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [entries]);

  const filteredCountBySection = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of filteredEntries) {
      counts.set(e.sectionName, (counts.get(e.sectionName) ?? 0) + 1);
    }
    return counts;
  }, [filteredEntries]);

  const tradeTagOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of entries) {
      if (!e.tradeTag) continue;
      counts.set(e.tradeTag, (counts.get(e.tradeTag) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag, count]) => ({ tag, count }));
  }, [entries]);

  const activeList = useMemo(() => {
    if (activeSection === null) return filteredEntries;
    return grouped.find((s) => s.name === activeSection)?.items ?? [];
  }, [grouped, filteredEntries, activeSection]);

  const toggleSectionOpen = useCallback((name: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // A section renders its body when any of these are true:
  //   - user manually expanded it
  //   - user selected it via the chip strip (single-section view)
  //   - a search / trade-tag filter is active (auto-expand matches)
  const isSectionOpen = useCallback(
    (name: string) =>
      activeSection === name ||
      expandedSections.has(name) ||
      isFiltering,
    [activeSection, expandedSections, isFiltering],
  );

  const expandAll = useCallback(() => {
    setExpandedSections(new Set(grouped.map((s) => s.name)));
  }, [grouped]);
  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
  }, []);
  const allSectionsOpen =
    grouped.length > 0 && grouped.every((s) => expandedSections.has(s.name));

  if (loading && entries.length === 0) {
    return <LibrarySkeleton />;
  }
  if (error && entries.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <p className="ts-title-sub" style={{ color: 'var(--safety-fg)' }}>{error}</p>
        <button type="button" className="ts-btn ts-btn--secondary" onClick={() => reload()}>
          Retry
        </button>
      </div>
    );
  }

  const totalUses = entries.reduce((n, e) => n + e.useCount, 0);

  const handleCreateSection = async () => {
    const name = newSectionDraft.trim();
    if (!name) return;
    setCreatingSection(false);
    setNewSectionDraft('');
    const entry = await create({
      title: 'New entry',
      section_name: name,
      severity: null,
    });
    if (entry) {
      setActiveSection(entry.sectionName);
      setExpandedId(entry.id);
    }
  };

  const handleAddEntry = async (sectionName: string) => {
    const entry = await create({
      title: 'New entry',
      section_name: sectionName,
      severity: null,
    });
    if (entry) {
      setActiveSection(entry.sectionName);
      setExpandedId(entry.id);
    }
  };

  const handleReorderSection = (sectionName: string, ids: string[]) => {
    reorder(sectionName, ids);
  };

  const closeConfirm = () => { if (!busy) setConfirm(null); };
  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'delete') {
        await remove(confirm.entry.id);
        if (expandedId === confirm.entry.id) setExpandedId(null);
      }
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="ts-title-row">
        <div>
          <h1 className="ts-title">Deficiency Library</h1>
          <div className="ts-title-sub">
            {seededStartersOnly ? (
              <>
                {starters?.length ?? 0} starter{' '}
                {(starters?.length ?? 0) === 1 ? 'entry' : 'entries'} ·{' '}
                {starterGroups.length}{' '}
                {starterGroups.length === 1 ? 'section' : 'sections'} ·{' '}
                tap <em>Add to library</em> to personalize
              </>
            ) : (
              <>
                {entries.length} {entries.length === 1 ? 'entry' : 'entries'} ·{' '}
                {grouped.length} {grouped.length === 1 ? 'section' : 'sections'}{' '}
                · {totalUses} {totalUses === 1 ? 'use' : 'uses'} total
              </>
            )}
          </div>
        </div>
        <div className="ts-head-actions">
          <AutosaveIndicator state={saveState} onRetry={() => reload()} />
        </div>
      </div>

      {entries.length === 0 && introDismissed === false ? (
        <div className="dl-intro" role="note">
          <div className="dl-intro-body">
            <div className="dl-intro-eyebrow">Welcome</div>
            <div className="dl-intro-title">
              {seededStartersOnly
                ? 'Your starter library is ready to personalize.'
                : 'Your reusable observations live here.'}
            </div>
            <div className="dl-intro-text">
              {seededStartersOnly ? (
                <>
                  We&rsquo;ve pre-loaded {starters?.length ?? 0} starter
                  observations grouped by section. Tap <em>Add to library</em>{' '}
                  on any starter to keep it &mdash; once added you can edit
                  the wording to match how you write.
                </>
              ) : (
                <>
                  Save the observations you write over and over &mdash; they
                  become one-tap chips during field capture, grouped by
                  section. Browse starter suggestions below, or create your
                  own.
                </>
              )}
            </div>
            <div className="dl-intro-actions">
              <button
                type="button"
                className="ts-btn ts-btn--primary ts-btn--sm"
                onClick={handleDismissIntro}
              >
                Got it
              </button>
              <button
                type="button"
                className="ts-btn ts-btn--secondary ts-btn--sm"
                onClick={handleBrowseSuggestions}
              >
                {seededStartersOnly ? 'Browse starter library' : 'Browse suggestions'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="dl-intro" role="note">
          <div className="dl-intro-body">
            <div className="dl-intro-eyebrow">One-tap findings</div>
            <div className="dl-intro-title">
              Save the observations you write over and over
            </div>
            <div className="dl-intro-text">
              Grouped by section name &mdash; the same names as your template.
              Entries surface as chips during field capture and become a finding
              on tap.
            </div>
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <div className="dl-filters">
          <div className="dl-search">
            <input
              type="search"
              className="dl-search-input"
              placeholder="Search titles, descriptions, or components…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search library"
            />
            {searchQuery && (
              <button
                type="button"
                className="ts-btn ts-btn--secondary ts-btn--sm"
                onClick={() => setSearchQuery('')}
              >
                Clear
              </button>
            )}
            <span className="dl-search-meta">
              {isFiltering
                ? `${filteredEntries.length} of ${entries.length}`
                : `${entries.length} total`}
            </span>
          </div>

          {tradeTagOptions.length > 0 && (
            <div className="dl-trade-filters" role="tablist" aria-label="Trade">
              <button
                type="button"
                role="tab"
                aria-selected={activeTradeTag === null}
                className={
                  'dl-trade-chip' + (activeTradeTag === null ? ' dl-trade-chip--on' : '')
                }
                onClick={() => setActiveTradeTag(null)}
              >
                all trades
              </button>
              {tradeTagOptions.map(({ tag, count }) => (
                <button
                  key={tag}
                  type="button"
                  role="tab"
                  aria-selected={activeTradeTag === tag}
                  className={
                    'dl-trade-chip' + (activeTradeTag === tag ? ' dl-trade-chip--on' : '')
                  }
                  onClick={() =>
                    setActiveTradeTag((prev) => (prev === tag ? null : tag))
                  }
                >
                  {tag} <span className="dl-trade-count">{count}</span>
                </button>
              ))}
            </div>
          )}

          <div className="dl-sections" role="tablist" aria-label="Sections">
            <button
              type="button"
              role="tab"
              aria-selected={activeSection === null}
              className={
                'dl-section-chip' + (activeSection === null ? ' dl-section-chip--on' : '')
              }
              onClick={() => setActiveSection(null)}
            >
              All <span className="dl-section-count">{filteredEntries.length}</span>
            </button>
            {allSectionNames.map((name) => {
              const count = filteredCountBySection.get(name) ?? 0;
              const dim = isFiltering && count === 0;
              return (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={activeSection === name}
                  className={
                    'dl-section-chip' +
                    (activeSection === name ? ' dl-section-chip--on' : '') +
                    (dim ? ' dl-section-chip--dim' : '')
                  }
                  onClick={() => setActiveSection(name)}
                >
                  {name} <span className="dl-section-count">{count}</span>
                </button>
              );
            })}
          </div>

          {activeSection === null && grouped.length > 1 && !isFiltering && (
            <div className="dl-section-bulk">
              <button
                type="button"
                className="ts-btn ts-btn--secondary ts-btn--sm"
                onClick={allSectionsOpen ? collapseAll : expandAll}
              >
                {allSectionsOpen ? 'Collapse all' : 'Expand all'}
              </button>
            </div>
          )}
        </div>
      )}

      {activeSection === null ? (
        <>
          {entries.length === 0 && !seededStartersOnly && (
            <EmptyState
              onCreate={() => setCreatingSection(true)}
              creating={creatingSection}
              draft={newSectionDraft}
              setDraft={setNewSectionDraft}
              onSubmit={handleCreateSection}
              onCancel={() => { setCreatingSection(false); setNewSectionDraft(''); }}
            />
          )}
          {entries.length > 0 && grouped.length === 0 && (
            <div className="dl-no-matches">
              No entries match this filter. Try a different search or clear the
              trade filter.
            </div>
          )}
          {grouped.map((sec) => (
            <SectionCard
              key={sec.name}
              sectionName={sec.name}
              entries={sec.items}
              open={isSectionOpen(sec.name)}
              onToggle={() => toggleSectionOpen(sec.name)}
              expandedId={expandedId}
              onExpand={(id) => setExpandedId((prev) => (prev === id ? null : id))}
              onPatch={(id, patch) => update(id, patch)}
              onRename={(id, title) => update(id, { title })}
              onMoveTo={(id, target) => update(id, { section_name: target })}
              onDelete={(entry) => setConfirm({ kind: 'delete', entry })}
              onReorder={(ids) => handleReorderSection(sec.name, ids)}
              onAdd={() => handleAddEntry(sec.name)}
              sections={allSectionNames}
            />
          ))}
          {entries.length > 0 && !isFiltering && (
            <AddSection
              creating={creatingSection}
              draft={newSectionDraft}
              setDraft={setNewSectionDraft}
              onStart={() => setCreatingSection(true)}
              onSubmit={handleCreateSection}
              onCancel={() => { setCreatingSection(false); setNewSectionDraft(''); }}
            />
          )}
        </>
      ) : (
        <SectionCard
          sectionName={activeSection}
          entries={activeList}
          open
          onToggle={() => setActiveSection(null)}
          expandedId={expandedId}
          onExpand={(id) => setExpandedId((prev) => (prev === id ? null : id))}
          onPatch={(id, patch) => update(id, patch)}
          onRename={(id, title) => update(id, { title })}
          onMoveTo={(id, target) => update(id, { section_name: target })}
          onDelete={(entry) => setConfirm({ kind: 'delete', entry })}
          onReorder={(ids) => handleReorderSection(activeSection, ids)}
          onAdd={() => handleAddEntry(activeSection)}
          sections={allSectionNames}
        />
      )}

      {starterGroups.length > 0 && (
        <div ref={suggestionsAnchorRef} className="dl-suggestions-block">
          <div className="dl-suggestions-head">
            <h2 className="dl-suggestions-title">
              {seededStartersOnly ? 'Starter library' : 'Suggestions'}
            </h2>
            <span className="ts-title-sub">
              {starters?.length ?? 0} starter{' '}
              {(starters?.length ?? 0) === 1 ? 'entry' : 'entries'} available
              &mdash; tap <em>Add to library</em> to keep one.
            </span>
          </div>
          {starterGroups.map((group) => (
            <StarterSectionCard
              key={group.name}
              sectionName={group.name}
              items={group.items}
              expanded={expandedStarterSections.has(group.name)}
              onToggle={() => toggleStarterSection(group.name)}
              expandedStarterId={expandedStarterId}
              onToggleStarter={(id) =>
                setExpandedStarterId((prev) => (prev === id ? null : id))
              }
              onClone={handleCloneStarter}
              cloningId={cloningId}
            />
          ))}
        </div>
      )}

      {confirm?.kind === 'delete' && (
        <ConfirmModal
          title="Delete this entry?"
          body={
            <>
              Delete <strong>&ldquo;{confirm.entry.title}&rdquo;</strong> from your library.
              Existing findings that were applied from this entry will not be affected.
            </>
          }
          confirmLabel="Delete entry"
          busy={busy}
          onCancel={closeConfirm}
          onConfirm={runConfirm}
        />
      )}
    </>
  );
}

